import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { DATE_STORE_KEY_FORMAT } from '../../Common/settings/dateFormats'
import { RootState } from '../../store'
import { getUserSettings } from '../settings/userSettings'
import {
  ICalendarEvents,
  ICalendarSettings,
  IEventPositionChange,
  IGetBlockReturn,
  IGetBlocksReturn,
  TOpenEventDetails,
} from '../types'

import { addAppointmentActivity } from './thunks/addAppointmentActivity'
import { createAppointment } from './thunks/createAppointment'
import { appointmentDelete } from './thunks/deleteAppointment'
import { fetchAppointment } from './thunks/fetchAppointment'
import { fetchCalendar } from './thunks/fetchCalendar'
import { updateAppointmentNotes } from './thunks/updateApointmentNotes'
import { updateAppointmentActivity } from './thunks/updateAppointmentActivity'
import { appointmentForCalendarTransformer } from './transformer/appointmentForCalendarTransformer'
import { blockForCalendarTransformer } from './transformer/blockForCalendarTransformer'

export interface ICalendarState extends ICalendarEvents {
  settings: ICalendarSettings
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null | string
}
export const initialEventPositionChange: IEventPositionChange = {
  uuid: null,
  state: 'idle',
}

export const initialState: ICalendarState = {
  eventsByDay: {},
  openFormDetails: {
    type: 'idle',
    event: null,
  },
  eventPositionChange: initialEventPositionChange,
  status: 'idle',
  settings: getUserSettings(),
  error: null,
}

export const calendarSlice = createSlice({
  name: 'calendarAppointments',
  initialState,
  reducers: {
    setAmountOfCalendarDays: (
      state,
      action: PayloadAction<{ amountOfCalendarDays: 1 | 3 | 7 }>,
    ) => {
      state.settings.amountOfCalendarDays = action.payload.amountOfCalendarDays
    },
    setColorContrast: (
      state,
      action: PayloadAction<{
        colorContrast: string
        colorContrastValue: string
      }>,
    ) => {
      state.settings.colorContrast = action.payload.colorContrast
      state.settings.colorContrastValue = action.payload.colorContrastValue
    },
    setInActivePersonIds: (
      state,
      action: PayloadAction<{ inActivePersonIds: number[] }>,
    ) => {
      state.settings.inActivePersonIds = action.payload.inActivePersonIds
    },
    setZoomLevelValue: (
      state,
      action: PayloadAction<{ zoomLevelValue: string }>,
    ) => {
      state.settings.zoomLevelValue = action.payload.zoomLevelValue
    },
    setOpenEventDetails: (
      state,
      action: PayloadAction<{ openFormDetails: TOpenEventDetails }>,
    ) => {
      state.openFormDetails = action.payload.openFormDetails
    },
    setEventPosition: (
      state,
      action: PayloadAction<{ eventPositionChange: IEventPositionChange }>,
    ) => {
      state.eventPositionChange = action.payload.eventPositionChange
    },
    resetEventPosition: state => {
      state.eventPositionChange = {
        uuid: null,
        state: 'idle',
      }
    },
  },
  extraReducers: builder => {
    // GET APPOINTMENTS
    builder
      .addCase(fetchCalendar.pending, state => {
        state.status = 'loading'
      })
      .addCase(fetchCalendar.fulfilled, (state, action) => {
        const newAppointments: ICalendarEvents =
          appointmentForCalendarTransformer(
            action.payload.list,
            action.payload.dateRange,
          )

        state.eventsByDay = Object.keys(newAppointments.eventsByDay).reduce(
          (prev, key) => {
            if (!prev[key]) {
              prev[key] = { appointments: [], blocks: [] }
            }

            prev[key].appointments =
              newAppointments.eventsByDay[key].appointments

            return prev
          },
          {} as ICalendarState['eventsByDay'],
        )
        state.status = 'succeeded'
      })
      // CREATE APPOINTMENT
      .addCase(createAppointment.pending, state => {
        state.status = 'loading'
      })
      .addCase(createAppointment.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
          ? action.payload.error
          : 'Create appointment failed'
      })
      // GET APPOINTMENT
      .addCase(fetchAppointment.pending, state => {
        state.status = 'loading'
      })
      .addCase(fetchAppointment.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
          ? action.payload.error
          : 'Get appointment failed.'
      })
      .addCase(
        fetchAppointment.fulfilled,
        (state, { payload: { data, type } }) => {
          state.status = 'succeeded'

          const appointmentDateKey: string = data.startsAt.format('YYYY-MM-DD')

          if (type === 'create') {
            if (!state.eventsByDay[appointmentDateKey])
              state.eventsByDay[appointmentDateKey] = {
                appointments: [],
                blocks: [],
              }

            state.eventsByDay[appointmentDateKey].appointments.push(data)
            return
          }

          const index = state.eventsByDay[
            appointmentDateKey
          ].appointments.findIndex(app => app.uuid === data.uuid)
          state.eventsByDay[appointmentDateKey].appointments[index] = data
        },
      )
      // DELETE APPOINTMENT
      .addCase(appointmentDelete.pending, state => {
        state.status = 'loading'
      })
      .addCase(appointmentDelete.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
          ? action.payload.error
          : 'Delete appointment failed.'
      })
      .addCase(
        appointmentDelete.fulfilled,
        (state, { payload: { appointmentDateKey, appointmentUuid } }) => {
          state.status = 'succeeded'
          state.eventsByDay[appointmentDateKey].appointments =
            state.eventsByDay[appointmentDateKey].appointments.filter(
              appointment => appointment.uuid !== appointmentUuid,
            )
        },
      )

      // APPOINTMENT NOTES
      .addCase(updateAppointmentNotes.pending, state => {
        state.status = 'loading'
      })
      .addCase(updateAppointmentNotes.rejected, state => {
        state.status = 'failed'
      })
      .addCase(
        updateAppointmentNotes.fulfilled,
        (
          state,
          { payload: { appointmentDateKey, appointmentUuid, appointmentNote } },
        ) => {
          state.status = 'succeeded'

          const updatedAppointment = state.eventsByDay[
            appointmentDateKey
          ].appointments.find(app => (app.uuid = appointmentUuid))

          if (updatedAppointment)
            updatedAppointment.appointmentNotes = appointmentNote
        },
      )
      // ADD APPOINTMENT ACTIVITY
      .addCase(addAppointmentActivity.pending, state => {
        state.status = 'loading'
      })
      .addCase(addAppointmentActivity.fulfilled, state => {
        state.status = 'succeeded'
      })
      // DRAG AND DROP APPOINTMENT && IS LOCKED TOGGLE
      .addCase(updateAppointmentActivity.pending, state => {
        state.status = 'loading'
      })
      .addCase(updateAppointmentActivity.fulfilled, state => {
        state.status = 'succeeded'
      })
      .addMatcher(
        action =>
          action.type.includes('appointment') || action.type.includes('block'),
        (state, action: PayloadAction<{ type: string }>) => {
          const closeFormWindowActionType: string[] = [
            'appointment/get/fulfilled',
            'appointment/delete/fulfilled',
            'block/get/fulfilled',
            'block/delete/fulfilled',
            'appointment/notes/post/fulfilled',
          ]
          if (
            closeFormWindowActionType.includes(action.type) &&
            action.payload.type !== 'update'
          ) {
            state.openFormDetails = { type: 'idle', event: null }
          }
        },
      )
      .addMatcher(
        action => action.type.endsWith('blocks/get/fulfilled'),
        (state, action: PayloadAction<IGetBlocksReturn>) => {
          const newBlocks: ICalendarEvents = blockForCalendarTransformer(
            action.payload.list,
            action.payload.dateRange,
          )

          state.eventsByDay = Object.keys(newBlocks.eventsByDay).reduce(
            (prev, key) => {
              if (!prev[key]) {
                prev[key] = { appointments: [], blocks: [] }
              }

              prev[key].blocks = newBlocks.eventsByDay[key].blocks

              return prev
            },
            {} as ICalendarState['eventsByDay'],
          )
          state.status = 'succeeded'
        },
      )
      .addMatcher(
        action => action.type.endsWith('block/get/fulfilled'),
        (
          state,
          { payload: { type, data } }: PayloadAction<IGetBlockReturn>,
        ) => {
          const appointmentDateKey: string = data.startsAt.format(
            DATE_STORE_KEY_FORMAT,
          )
          state.status = 'succeeded'

          if (type === 'create') {
            if (!state.eventsByDay[appointmentDateKey])
              state.eventsByDay[appointmentDateKey] = {
                appointments: [],
                blocks: [],
              }

            state.eventsByDay[appointmentDateKey].blocks.push(data)
            return
          }

          state.eventsByDay[appointmentDateKey].blocks.filter(
            block => block.uuid !== data.uuid,
          )
        },
      )
  },
})

export const calendarStateSelector = (state: RootState): ICalendarState =>
  state.calendarNew
export const calendarSettingsSelector = (state: RootState) =>
  calendarStateSelector(state).settings
export const calendarOpenFormDetailsSelector = (state: RootState) =>
  calendarStateSelector(state).openFormDetails
export const calendarStatusSelector = (state: RootState) =>
  calendarStateSelector(state).status

export const eventPositionChange = (state: RootState) =>
  calendarStateSelector(state).eventPositionChange

export const {
  setAmountOfCalendarDays,
  setColorContrast,
  setInActivePersonIds,
  setZoomLevelValue,
  setOpenEventDetails,
  setEventPosition,
  resetEventPosition,
} = calendarSlice.actions

export const calendarReducer = calendarSlice.reducer
